import {
  Button,
  createTheme,
  CSSVariablesResolver,
  LoadingOverlay,
  MantineProvider,
  Modal,
  MultiSelect,
  Select,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import React from 'react';

import AirbrakeErrorBoundary from './AirbrakeBoundary';
import ApolloClientProvider, { ApolloClientProviderProps } from './ApolloClientProvider';
import { SHARED_COLORS } from './theme-colors';

export type AdminProviderProps = ApolloClientProviderProps & {
  // this prop should be used when there is a more than one component at the same page
  // because it reads the same store for notifications
  withNotification?: boolean;
};

const AdminProvider: React.FC<AdminProviderProps> = ({
  children,
  clientOptions,
  withNotification = true,
}) => (
  <AirbrakeErrorBoundary>
    <ApolloClientProvider clientOptions={clientOptions}>
      <MantineProvider theme={theme} cssVariablesResolver={resolver} defaultColorScheme="light">
        {withNotification ? (
          <ModalsProvider modalProps={{ zIndex: 1300 }}>
            <Notifications position="bottom-right" autoClose={2000} zIndex={1400} />
            {children}
          </ModalsProvider>
        ) : (
          children
        )}
      </MantineProvider>
    </ApolloClientProvider>
  </AirbrakeErrorBoundary>
);

export default AdminProvider;

const theme = createTheme({
  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  fontSmoothing: false,
  cursorType: 'pointer',
  colors: { warning: SHARED_COLORS.warning },
  components: {
    Modal: Modal.extend({
      defaultProps: {
        overlayProps: { backgroundOpacity: 0.55, blur: 3 },
        transitionProps: { duration: 300 },
        zIndex: 1100,
      },
    }),
    LoadingOverlay: LoadingOverlay.extend({
      defaultProps: {
        overlayProps: { blur: 2 },
        loaderProps: { type: 'dots' },
      },
    }),
    Button: Button.extend({
      defaultProps: {
        loaderProps: { type: 'dots' },
      },
    }),
    Select: Select.extend({
      defaultProps: {
        comboboxProps: { zIndex: 1200 },
      },
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        comboboxProps: { zIndex: 1200 },
      },
    }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        popoverProps: { zIndex: 1200 },
      },
    }),
    Tooltip: Tooltip.extend({
      defaultProps: {
        zIndex: 1200,
      },
    }),
  },
});

const resolver: CSSVariablesResolver = (theme) => ({
  variables: {
    '--snappea-color-body': '#FFFFFF',
    '--snappea-color-text': '#333333',
    '--snappea-font-size-text': theme.fontSizes.sm,
  },
  light: {},
  dark: {},
});
